import { type RefObject, useCallback, useEffect } from 'react';

type MaybeElementRef<T extends HTMLElement | SVGElement> =
  | RefObject<T | null>
  | T
  | null
  | undefined;

type UseOnClickOutsideOptions = {
  shouldIgnoreEvent?: (event: MouseEvent) => boolean;
};

const resolveElement = <T extends HTMLElement | SVGElement>(el: MaybeElementRef<T>): T | null => {
  if (el == null) {
    return null;
  }
  if (typeof el === 'object' && 'current' in el) {
    return (el as RefObject<T | null>).current;
  }
  return el as T;
};

/**
 * Registers a click event listener on the document to check if the click is outside of the
 * supplied element(s). If true, the callback function is called.
 *
 * @param el One or more elements or refs; refs are read at click time (not during render)
 * @param onClickOutside Callback function to call when the click is outside of the element(s)
 */
const useOnClickOutside = <T extends HTMLElement | SVGElement>(
  el: MaybeElementRef<T> | MaybeElementRef<T>[],
  onClickOutside: (event: MouseEvent) => void,
  options?: UseOnClickOutsideOptions,
) => {
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      const target = event.target as Node;

      // Do nothing if the target is not contained in the document
      if (!target || !target.isConnected) {
        return;
      }

      if (options?.shouldIgnoreEvent?.(event)) {
        return;
      }

      const items = Array.isArray(el) ? el : [el];
      const resolved = items.map(resolveElement).filter((element): element is T => element != null);

      const isOutside =
        resolved.length === 0 ? true : resolved.every((element) => !element.contains(target));

      if (isOutside) {
        onClickOutside(event);
      }
    },
    // eslint-disable-next-line react-hooks/use-memo, react-hooks/exhaustive-deps
    [...(Array.isArray(el) ? el : [el]), options?.shouldIgnoreEvent],
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside, { capture: true });

    return () => document.removeEventListener('mousedown', handleClickOutside, { capture: true });
  }, [handleClickOutside]);
};

export default useOnClickOutside;
